import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";

interface DangerCatchData {
  name?: string;
  text_success: string;
  text_fail: string;
}

interface DangerCatchDetailProps {
  score: number; // 0 or 1
  contentId?: number;
}

const DangerCatchDetail = ({ score, contentId }: DangerCatchDetailProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<DangerCatchData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const isSuccess = score === 1;

  // Fetch content-danger-catch data
  useEffect(() => {
    const fetchData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${apiUrls.stats.contentSpecificData}?contentId=${contentId}&contentType=content-danger-catch`,
        );

        // Note: axios interceptor in auth.ts auto-unwraps response.data.data to response.data
        const catchData = response.data;

        if (catchData) {
          setData({
            name: catchData.name,
            text_success: catchData.text_success || "",
            text_fail: catchData.text_fail || "",
          });
        }
      } catch (err) {
        console.error("Error fetching danger catch content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  const explanation = (isSuccess ? data.text_success : data.text_fail)?.trim();

  return (
    <div className="h-full flex flex-col">
      <div className="border-2 border-teal-200 rounded-lg p-4 flex-1 overflow-y-auto">
        {/* Result status */}
        <div className="flex items-center gap-3 mb-4">
          {isSuccess ? (
            <>
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-lg font-medium text-green-700">
                {t("statsNew.interaction.dangerCatch.success")}
              </span>
            </>
          ) : (
            <>
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <span className="text-lg font-medium text-red-700">
                {t("statsNew.interaction.dangerCatch.fail")}
              </span>
            </>
          )}
        </div>

        {/* Explanation */}
        {explanation && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">
              {t("statsNew.interaction.explanation")}
            </p>
            <div className={`p-3 rounded-lg ${isSuccess ? "bg-green-50" : "bg-red-50"}`}>
              <p className={`text-sm ${isSuccess ? "text-green-700" : "text-red-700"}`}>
                {explanation}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DangerCatchDetail;
